
import { ContactViewModel } from '../ViewModels';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';

import { ContactHeader } from '../contact-config';
import { useContactEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useContact } from '@/OneLiners';
import { ContactModel } from '../ContactModel';

/**
 * useContactTableConfig hook
 *
 * Domain-specific layout composition hook for Contact entities.
 * Acts as the composition layer between:
 * - Contact domain data access,
 * - enrichment logic and relationship resolution,
 * - permission state aggregation,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Aggregate domain and enrichment readiness states into a single contract.
 * - Provide dynamic header configuration for the Contact table.
 * - Define strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<ContactViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<ContactViewModel>
 */
export function useContactTableConfig(props: DomainHookProps): DomainTableConfig<ContactViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Contact;

    /**
     * Domain enums used for headers and VM mapping.
     */
    const { ContactEnums, NeedEnums, MatchingEnums } = useAppEnum();

    /**
     * Contact domain entry point.
     */
    const contactHook = useContact();

    /**
     * Contact enrichment layer.
     *
     * Resolves user relationships and computes derived Contact fields.
     */
    const {
        userMap,
        getStrictEnrichedContact,
        enricherResponse,
    } = useContactEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle initializes only once this
     * contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            contactHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: contactHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
        * Delegation to generic layout lifecycle.
        */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: contactHook,

        /**
         * Header configuration for Property table.
         */
        headers: ContactHeader.build(userMap, ContactEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            contact: ContactModel, isDisplaying: boolean) =>
            new ContactViewModel({
                contact: getStrictEnrichedContact(contact),
                viewEnums: ContactEnums,
                needEnums: NeedEnums,
                matchingEnums: MatchingEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}